/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

import type { LeaseAction } from '../constants/lease-states';
import type {
  CreateLeaseInput,
  Lease,
  LeaseDocumentCategory,
  LeaseExpectation,
  LeaseExpectationsResponse,
  LeaseListResponse,
  LeaseReceipt,
  LeaseSettings,
  WarrantyDecision,
} from '../types/lease.types';

/**
 * Typed client for the lease registry.
 *
 * The name is deliberately singular. A dead-code guard
 * (`src/shared/__test__/no-leases-service-import.test.ts`) bans the plural
 * spelling anywhere under `src/` -- it belonged to scaffolding deleted
 * earlier -- and its pattern matches across a whole file, so merely naming
 * it in a comment is enough to fail the check. Which is how this very
 * docstring first tripped it.
 */
export class LeaseService {
  /**
   * List the agency's contracts.
   *
   * `overdue` is scanned server-side and costs one read per contract on
   * the page, so it is sent only when the caller asked for it -- the
   * overview's "aluguéis em atraso" tile is the one that does.
   */
  public static listLeases(
    agencyId: string,
    state?: string,
    page: number = 1,
    pageSize: number = 50,
    overdue?: boolean,
  ): CancelablePromise<LeaseListResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases',
      path: { agencyId },
      query: {
        page, page_size: pageSize,
        ...(state && { state }),
        ...(overdue ? { overdue: true } : {}),
      },
    });
  }

  /**
   * Open a lease draft.
   *
   * A lease does not need a won deal to exist: `origin_deal_id` defaults to
   * empty and is never required, so an agency that already had contracts
   * before adopting housien can register them directly. The auto-draft
   * opened when a rental deal is won is an additional door, not the only
   * one.
   *
   * The backend forces `state: "draft"`, so the lease still has to be
   * activated afterwards -- deliberately, because activation stamps
   * `activated_at` and `first_accrual_period` from the current date.
   */
  public static createLease(
    agencyId: string,
    body: CreateLeaseInput,
  ): CancelablePromise<Lease> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases',
      path: { agencyId },
      body,
      mediaType: 'application/json',
      // No `errors` map, for the same reason applyTransition has none: the
      // backend's 400 names the field the schema rejected, and a fixed
      // string here would throw that away.
    });
  }

  /** The agency's lease settings: due-date defaults and document vocabulary. */
  public static readSettings(agencyId: string): CancelablePromise<LeaseSettings> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/settings',
      path: { agencyId },
    });
  }

  /**
   * Writes the due-date defaults. A key left out is left alone; a key sent
   * as null is cleared back to the platform default.
   */
  public static updateSettings(
    agencyId: string,
    body: {
      default_payment_day?: number | null;
      business_day_rule?: string | null;
      shift_discount_deadline?: boolean | null;
    },
  ): CancelablePromise<LeaseSettings> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/leases/settings',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static addDocumentCategory(
    agencyId: string,
    body: { label: string },
  ): CancelablePromise<LeaseDocumentCategory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/settings/document-categories',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  public static updateDocumentCategory(
    agencyId: string,
    categoryId: string,
    body: { label: string },
  ): CancelablePromise<LeaseDocumentCategory> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/leases/settings/document-categories/{categoryId}',
      path: { agencyId, categoryId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getLease(agencyId: string, leaseId: string): CancelablePromise<Lease> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/{leaseId}',
      path: { agencyId, leaseId },
    });
  }

  /**
   * Fill in what a draft still lacks. The backend refuses the fields that
   * only move through their own doors (state, landlords, ids).
   */
  public static updateLease(
    agencyId: string,
    leaseId: string,
    body: { billing_locale?: string; warranty_decision?: WarrantyDecision },
  ): CancelablePromise<Lease> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agency/{agencyId}/leases/{leaseId}',
      path: { agencyId, leaseId },
      body,
      mediaType: 'application/json',
    });
  }

  public static listExpectations(
    agencyId: string,
    leaseId: string,
  ): CancelablePromise<LeaseExpectationsResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/{leaseId}/expectations',
      path: { agencyId, leaseId },
    });
  }

  /**
   * Record that rent arrived ("recebi"). A past fact, never a charge: no
   * boleto is issued and nothing is transferred.
   */
  public static recordReceipt(
    agencyId: string,
    leaseId: string,
    accrualPeriod: string,
    body: { amount_cents: number; received_on: string },
  ): CancelablePromise<LeaseReceipt> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/expectations/{accrualPeriod}/receipts',
      path: { agencyId, leaseId, accrualPeriod },
      body,
      mediaType: 'application/json',
    });
  }

  /** Forgive what is still owed on one month ("dei baixa"). */
  public static writeOff(
    agencyId: string,
    leaseId: string,
    accrualPeriod: string,
    body: { write_off_date: string; notes?: string },
  ): CancelablePromise<LeaseReceipt> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/expectations/{accrualPeriod}/write-off',
      path: { agencyId, leaseId, accrualPeriod },
      body,
      mediaType: 'application/json',
    });
  }

  /**
   * Confirm a first partial month. Omit the amount to confirm the computed
   * pro-rata as it stands.
   */
  public static confirmExpectation(
    agencyId: string,
    leaseId: string,
    accrualPeriod: string,
    body: { amount_cents?: number } = {},
  ): CancelablePromise<LeaseExpectation> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/expectations/{accrualPeriod}/confirm',
      path: { agencyId, leaseId, accrualPeriod },
      body,
      mediaType: 'application/json',
    });
  }

  /** Undo one receipt recorded by mistake, reopening its month. */
  public static reverseReceipt(
    agencyId: string,
    leaseId: string,
    accrualPeriod: string,
    receiptId: string,
  ): CancelablePromise<LeaseReceipt> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/expectations/{accrualPeriod}/receipts/{receiptId}/reverse',
      path: { agencyId, leaseId, accrualPeriod, receiptId },
    });
  }

  /**
   * Apply a lifecycle transition. The service validates the edge and
   * answers 422 naming the problem when it is not allowed, so the caller
   * only needs to surface the message.
   */
  public static applyTransition(
    agencyId: string,
    leaseId: string,
    action: LeaseAction,
    body: { from_date?: string; to_date?: string; reason?: string } = {},
  ): CancelablePromise<Lease> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/transitions',
      path: { agencyId, leaseId },
      body: { action, ...body },
      mediaType: 'application/json',
      // Deliberately no 422 entry: the backend answers it naming every
      // missing requirement ("Cannot activate: missing landlords,
      // billing_locale, warranty_decision"). An entry here would replace
      // that with a fixed string, discarding the only part of the response
      // that tells the broker what to go fix.
    });
  }
  public static listDocuments(
    agencyId: string,
    leaseId: string,
  ): CancelablePromise<{ data: Array<{ document_id: string; filename: string; category: string }> }> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/{leaseId}/documents',
      path: { agencyId, leaseId },
    });
  }

  public static createDocumentUploadLink(
    agencyId: string,
    leaseId: string,
    body: { filename: string; content_type: string; category?: string; label?: string },
  ): CancelablePromise<{
    document_id: string;
    label?: string | null;
    upload_url: { url: string; fields?: Record<string, string> };
  }> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/leases/{leaseId}/documents/upload-link',
      path: { agencyId, leaseId },
      body,
      mediaType: 'application/json',
    });
  }

  public static getDocumentViewLink(
    agencyId: string,
    leaseId: string,
    documentId: string,
  ): CancelablePromise<{ url: string }> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/leases/{leaseId}/documents/{documentId}',
      path: { agencyId, leaseId, documentId },
    });
  }

  public static deleteDocument(
    agencyId: string,
    leaseId: string,
    documentId: string,
  ): CancelablePromise<{ deleted: boolean }> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/leases/{leaseId}/documents/{documentId}',
      path: { agencyId, leaseId, documentId },
    });
  }
}
